import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
import {
    PiEnvelopeSimple,
    PiClock,
    PiPulse,
    PiArrowRight
} from 'react-icons/pi';

// ============================================
// ANIMATIONS
// ============================================

const pulse = keyframes`
    0%, 100% { 
        box-shadow: 0 0 0 0 rgba(138, 43, 226, 0.4);
    }
    50% { 
        box-shadow: 0 0 20px 4px rgba(138, 43, 226, 0.2);
    }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Section = styled.section`
    position: relative;
    padding: 100px 24px;
    background-color: #fdfdfd;
    overflow: hidden;

    /* Old pattern replaced by component */
    background-color: #fdfdfd;
    overflow: hidden;
`;

// ... (keep Container etc) ...

// ============================================
// BACKGROUND ROUTE ANIMATION
// ============================================

const RouteContainer = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    opacity: 0.4;
`;

const BackgroundRoute = () => {
    // Generate a simple curved path that spans the width
    const path = "M 0 100 C 300 50, 600 150, 900 100 C 1200 50, 1500 150, 1800 100";

    return (
        <RouteContainer>
            <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1800 400" >
                <defs>
                    <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(138, 43, 226, 0.0)" />
                        <stop offset="50%" stopColor="rgba(138, 43, 226, 0.3)" />
                        <stop offset="100%" stopColor="rgba(138, 43, 226, 0.0)" />
                    </linearGradient>
                </defs>

                {/* Base Line */}
                <path
                    d={path}
                    fill="none"
                    stroke="url(#line-gradient)"
                    strokeWidth="2"
                    strokeDasharray="8 8"
                />

                {/* Moving Dot 1 */}
                <motion.circle
                    r="4"
                    fill="#8A2BE2"
                    initial={{ offsetDistance: "0%" }}
                    animate={{ offsetDistance: "100%" }}
                    style={{ offsetPath: `path('${path}')` }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        ease: "linear",
                        repeatDelay: 2
                    }}
                />

                {/* Moving Dot 2 (Delayed) */}
                <motion.circle
                    r="4"
                    fill="#3B82F6"
                    initial={{ offsetDistance: "0%" }}
                    animate={{ offsetDistance: "100%" }}
                    style={{ offsetPath: `path('${path}')` }}
                    transition={{
                        duration: 12,
                        repeat: Infinity,
                        ease: "linear",
                        delay: 5,
                        repeatDelay: 1
                    }}
                />
            </svg>
        </RouteContainer>
    );
};

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
`;

const SectionLabel = styled(motion.div)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    color: #6b7280;
    letter-spacing: 0.1em;

    span.dot {
        width: 6px;
        height: 6px;
        background: linear-gradient(135deg, #8A2BE2, #3B82F6);
        border-radius: 50%;
    }
`;

const SectionTitle = styled(motion.h2)`
    font-family: 'Space Grotesk', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 16px;
    letter-spacing: -0.02em;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-family: 'Inter', sans-serif;
    font-size: 1.1rem;
    color: #6b7280;
    max-width: 600px;
    line-height: 1.6;
    margin-bottom: 60px;
`;

// Journey Timeline
const Timeline = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr auto 1fr;
    gap: 0;
    align-items: stretch;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

// Stage Card
const StageCard = styled(motion.div) <{ isHighlighted?: boolean }>`
    position: relative;
    padding: 32px;
    background: ${props => props.isHighlighted
        ? 'linear-gradient(135deg, rgba(138, 43, 226, 0.03) 0%, rgba(255, 255, 255, 0.95) 100%)'
        : 'rgba(255, 255, 255, 0.8)'};
    border: 1px solid ${props => props.isHighlighted ? 'rgba(138, 43, 226, 0.3)' : '#e5e7eb'};
    border-radius: 2px;
    backdrop-filter: blur(4px);
    transition: all 0.3s ease;

    ${props => props.isHighlighted && `
        animation: ${pulse} 3s ease-in-out infinite;
    `}

    /* Technical corner brackets */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        border: 1px solid ${props => props.isHighlighted ? '#8A2BE2' : '#d1d5db'};
        transition: all 0.3s ease;
    }

    &::before {
        top: 8px;
        left: 8px;
        border-right: none;
        border-bottom: none;
    }

    &::after {
        bottom: 8px;
        right: 8px;
        border-left: none;
        border-top: none;
    }

    &:hover {
        border-color: ${props => props.isHighlighted ? '#8A2BE2' : '#d1d5db'};
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);

        &::before, &::after {
            border-color: ${props => props.isHighlighted ? '#8A2BE2' : '#9ca3af'};
        }
    }
`;

const StageNumber = styled.div<{ isHighlighted?: boolean }>`
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: ${props => props.isHighlighted ? '#8A2BE2' : '#9ca3af'};
    letter-spacing: 0.05em;
    margin-bottom: 20px;
`;

const StageIcon = styled.div<{ isHighlighted?: boolean }>`
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.isHighlighted
        ? 'linear-gradient(135deg, rgba(138, 43, 226, 0.1), rgba(59, 130, 246, 0.1))'
        : '#f3f4f6'};
    border-radius: 8px;
    margin-bottom: 20px;

    svg {
        width: 24px;
        height: 24px;
        color: ${props => props.isHighlighted ? '#8A2BE2' : '#6b7280'};
    }
`;

const StageLabel = styled.div<{ isHighlighted?: boolean }>`
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: ${props => props.isHighlighted ? '#8A2BE2' : '#9ca3af'};
    letter-spacing: 0.1em;
    margin-bottom: 12px;
    text-transform: uppercase;
`;

const StageHeadline = styled.h3`
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.35rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 12px;
    line-height: 1.3;
`;

const StageDescription = styled.p`
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    color: #6b7280;
    line-height: 1.6;
`;

const SystemAnnotation = styled.div`
    margin-top: 20px;
    padding: 8px 12px;
    background: rgba(138, 43, 226, 0.05);
    border-left: 2px solid #8A2BE2;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: #8A2BE2;
    letter-spacing: 0.05em;
`;

// Connector
const Connector = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    position: relative;

    @media (max-width: 900px) {
        display: none;
    }
`;

const ConnectorLine = styled(motion.div)`
    height: 1px;
    width: 60px;
    background: repeating-linear-gradient(
        90deg,
        #d1d5db 0px,
        #d1d5db 4px,
        transparent 4px,
        transparent 8px
    );
    position: relative;

    &::after {
        content: '';
        position: absolute;
        right: -4px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid #d1d5db;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
    }
`;

// Bottom CTA hint
const BottomNote = styled(motion.div)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 48px;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    color: #6b7280;

    a {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: #111827;
        font-weight: 500;
        text-decoration: none;
        transition: color 0.2s ease;

        &:hover {
            color: #8A2BE2;
        }

        svg {
            transition: transform 0.2s ease;
        }

        &:hover svg {
            transform: translateX(4px);
        }
    }
`;

// ============================================
// DATA
// ============================================

const stages = [
    {
        number: '01',
        label: 'THE OLD WAY',
        icon: PiEnvelopeSimple,
        headline: 'Reactive support is a losing game',
        description: 'You wait for tickets. Customers wait for answers. Everyone loses.',
        isHighlighted: false,
    },
    {
        number: '02',
        label: 'THE REALITY',
        icon: PiClock,
        headline: 'By the time they ask, it\'s already too late',
        description: '67% of users churn silently. They don\'t complain — they just leave.',
        isHighlighted: false,
    },
    {
        number: '03',
        label: 'THE NEW ERA',
        icon: PiPulse,
        headline: 'Meet your AI Customer Success Agent',
        description: 'Detects user struggles in real-time. Engages proactively. Resolves before frustration.',
        isHighlighted: true,
        annotation: 'SYSTEM: PREDICTIVE_MODE // ACTIVE',
    },
];

// ============================================
// COMPONENT
// ============================================

const FounderJourneySection: React.FC = () => {
    return (
        <Section>
            <BackgroundRoute />
            <Container>
                <SectionLabel
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <span className="dot" />
                    THE TRANSFORMATION
                </SectionLabel>

                <SectionTitle
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    From reactive to predictive
                </SectionTitle>

                <SectionSubtitle
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    The old playbook doesn't work anymore. Here's why the best founders are switching to proactive customer success.
                </SectionSubtitle>

                <Timeline>
                    {stages.map((stage, index) => (
                        <React.Fragment key={stage.number}>
                            <StageCard
                                isHighlighted={stage.isHighlighted}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.3 + index * 0.15 }}
                            >
                                <StageNumber isHighlighted={stage.isHighlighted}>
                                    {stage.number}
                                </StageNumber>
                                <StageIcon isHighlighted={stage.isHighlighted}>
                                    <stage.icon />
                                </StageIcon>
                                <StageLabel isHighlighted={stage.isHighlighted}>
                                    {stage.label}
                                </StageLabel>
                                <StageHeadline>{stage.headline}</StageHeadline>
                                <StageDescription>{stage.description}</StageDescription>
                                {stage.annotation && (
                                    <SystemAnnotation>{stage.annotation}</SystemAnnotation>
                                )}
                            </StageCard>

                            {index < stages.length - 1 && (
                                <Connector>
                                    <ConnectorLine
                                        initial={{ scaleX: 0 }}
                                        whileInView={{ scaleX: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.5 + index * 0.2 }}
                                        style={{ transformOrigin: 'left' }}
                                    />
                                </Connector>
                            )}
                        </React.Fragment>
                    ))}
                </Timeline>

                <BottomNote
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 1 }}
                >
                    See what SmushChat can do with your business knowledge.
                    <a href="/get-started">
                        Start building <PiArrowRight />
                    </a>
                </BottomNote>
            </Container>
        </Section>
    );
};

export default FounderJourneySection;
